/**
 * Memoket Analytics Module
 * Handles event tracking for Ptengine, Klaviyo, and other analytics services
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  var MemoketAnalytics = {
    config: {
      debug: false,
      sessionId: null,
      ptengineReady: false,
      enableTimeEvents: false,
      enablePtengine: false
    },

    /** Queue for PTengine events when script is not loaded yet (deferred load) */
    ptengineQueue: [],

    /**
     * Ptengine event whitelist — only these events are sent to Ptengine
     * to control event volume and cost.
     *
     * PV/UV are tracked automatically by the Ptengine SDK itself.
     * InitiateCheckout and ab_variant_assigned are sent directly in their
     * respective modules (hero-a/b.liquid, cart-drawer/main-cart.liquid, ab-test.js).
     */
    ptengineAllowedEvents: [
      'cta_click',
      'add_to_cart',
      'remove_from_cart',
      'purchase'
    ],

    /**
     * Send event to PTengine now or queue for when PTengine is ready.
     * Only events in ptengineAllowedEvents are sent.
     */
    sendToPtengine: function(eventName, eventData) {
      if (!this.config.enablePtengine) {
        return;
      }
      // Whitelist filter — skip events not in the allowed list
      if (this.ptengineAllowedEvents.indexOf(eventName) === -1) {
        if (this.config.debug) {
          console.log('[Analytics] Ptengine skipped (not in whitelist):', eventName);
        }
        return;
      }
      if (window.ptengine && typeof window.ptengine.track === 'function') {
        this.config.ptengineReady = true;
        try {
          window.ptengine.track(eventName, eventData);
        } catch (e) {
          console.warn('[Analytics] Ptengine error:', e);
        }
      } else {
        this.ptengineQueue.push({ eventName: eventName, eventData: eventData });
      }
    },

    /**
     * Flush queued PTengine events (call when PTengine becomes available)
     */
    flushPtengineQueue: function() {
      if (!this.config.enablePtengine) return;
      if (!window.ptengine || typeof window.ptengine.track !== 'function') return;
      this.config.ptengineReady = true;
      while (this.ptengineQueue.length > 0) {
        var item = this.ptengineQueue.shift();
        // Double-check whitelist on flush (queue may contain old non-whitelisted events)
        if (this.ptengineAllowedEvents.indexOf(item.eventName) === -1) continue;
        try {
          window.ptengine.track(item.eventName, item.eventData);
        } catch (e) {
          console.warn('[Analytics] Ptengine flush error:', e);
        }
      }
      if (this.config.debug && this.ptengineQueue.length === 0) {
        console.log('[Analytics] PTengine queue flushed');
      }
    },

    /**
     * Poll for PTengine and flush queue (PTengine loads deferred, after our init)
     */
    initPtengineFlush: function() {
      if (!this.config.enablePtengine) return;
      var self = this;
      var attempts = 0;
      var maxAttempts = 50; // ~10s at 200ms
      var t = setInterval(function() {
        attempts++;
        if (window.ptengine && typeof window.ptengine.track === 'function') {
          clearInterval(t);
          self.flushPtengineQueue();
        } else if (attempts >= maxAttempts) {
          clearInterval(t);
        }
      }, 200);
    },

    /**
     * Initialize analytics
     */
    init: function() {
      this.config.sessionId = this.generateSessionId();

      // Start watching for PTengine so queued events get sent when it loads
      if (this.config.enablePtengine) {
        this.initPtengineFlush();
      }

      // Auto-tracking
      this.trackPageView();
      this.trackUniqueVisitor();
      this.initCTATracking();
      // NOTE: initSectionTracking() and initScrollTracking() are disabled here
      // to avoid duplicate Ptengine events. analytics-enhanced.js provides
      // superior versions (section_first_view + section_engagement, scroll_milestone)
      // that cover the same functionality with more detail.
      // this.initSectionTracking();
      // this.initScrollTracking();
      this.initUTMForwarding();
      if (this.config.enableTimeEvents) {
        this.trackSectionViewTime();
        this.trackTimeOnPage();
      }

      if (this.config.debug) {
        console.log('[Analytics] Initialized', this.config);
      }
    },

    /**
     * Generate unique session ID
     */
    generateSessionId: function() {
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    /**
     * Get UTM parameters from URL or sessionStorage.
     * On the landing page the URL carries UTM query-strings; we persist them
     * to sessionStorage so they survive internal navigations (product → cart
     * → checkout) within the same browser session.
     *
     * Priority: URL params (freshest) → sessionStorage (persisted earlier).
     * If the URL contains new UTM params they overwrite the stored values.
     */
    getUTMParams: function() {
      var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      try {
        var params = new URLSearchParams(window.location.search);
        var hasURLUTM = utmKeys.some(function(k) { return !!params.get(k); });

        if (hasURLUTM) {
          var result = {};
          utmKeys.forEach(function(k) { result[k] = params.get(k) || ''; });
          // Persist to sessionStorage so subsequent pages can read them
          try { sessionStorage.setItem('memoket_utm_params', JSON.stringify(result)); } catch (_) {}
          return result;
        }

        // Fallback: read from sessionStorage (set on the landing page)
        var stored = sessionStorage.getItem('memoket_utm_params');
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        // sessionStorage or JSON parse not available
      }
      return {};
    },

    /**
     * Get visitor ID from localStorage or create new
     */
    getVisitorId: function() {
      try {
        var visitorId = localStorage.getItem('memoket_visitor_id');
        if (!visitorId) {
          visitorId = 'v_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('memoket_visitor_id', visitorId);
        }
        return visitorId;
      } catch (e) {
        return 'anonymous';
      }
    },

    /**
     * Track an event
     */
    track: function(eventName, params) {
      params = params || {};

      var eventData = {
        event: eventName,
        timestamp: new Date().toISOString(),
        session_id: this.config.sessionId,
        visitor_id: this.getVisitorId(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: document.referrer
      };

      // Merge UTM params
      var utmParams = this.getUTMParams();
      for (var key in utmParams) {
        if (utmParams[key]) {
          eventData[key] = utmParams[key];
        }
      }

      var suppressMeta = params._suppress_meta === true;

      // Merge custom params
      for (var param in params) {
        if (param === '_suppress_meta') continue;
        eventData[param] = params[param];
      }

      if (this.config.debug) {
        console.log('[Analytics] Track:', eventName, eventData);
      }

      // Send to Ptengine (queue if not ready yet - script loads deferred)
      this.sendToPtengine(eventName, eventData);

      // Send to Klaviyo
      if (window._learnq) {
        try {
          window._learnq.push(['track', eventName, eventData]);
        } catch (e) {
          console.warn('[Analytics] Klaviyo error:', e);
        }
      }

      // Send to Google Analytics 4 (if available)
      if (window.gtag) {
        try {
          window.gtag('event', eventName, eventData);
        } catch (e) {
          console.warn('[Analytics] GA4 error:', e);
        }
      }

      // Send to Meta Pixel (Facebook Pixel)
      if (window.fbq && !suppressMeta) {
        try {
          // Map custom events to Meta Pixel standard events
          var metaEventName = this.mapToMetaPixelEvent(eventName);
          var pixelParams = this.prepareMetaPixelParams(eventData);
          var capiEventName = metaEventName || eventName;

          // Generate unique event ID for deduplication with Conversions API
          var eventId = eventName + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

          // Track with event ID for proper deduplication
          if (metaEventName) {
            window.fbq('track', metaEventName, pixelParams, {eventID: eventId});
          } else {
            // Track custom events
            window.fbq('trackCustom', eventName, pixelParams, {eventID: eventId});
          }

          // Log if in test mode
          if (window.META_TEST_EVENT_CODE) {
            console.log('[Meta Pixel] Event tracked:', capiEventName, pixelParams, 'eventID:', eventId);
          }

          // Send selected standard conversion events to Meta CAPI.
          // Custom interaction events remain Pixel-only to avoid polluting CAPI.
          if (this.shouldSendMetaCAPI(capiEventName)) {
            this.sendToMetaCAPI(capiEventName, eventId, eventData);
          }
        } catch (e) {
          console.warn('[Analytics] Meta Pixel error:', e);
        }
      }

      return eventData;
    },

    /**
     * Map custom event names to Meta Pixel standard events
     */
    mapToMetaPixelEvent: function(eventName) {
      var mapping = {
        // NOTE: 'page_view' is intentionally excluded here.
        // PageView is already tracked via fbq('track', 'PageView') in theme.liquid.
        // Including it here would cause duplicate PageView events in Meta.
        'view_item': 'ViewContent',
        'add_to_cart': 'AddToCart',
        'purchase': 'Purchase',
        'lead': 'Lead',
        'complete_registration': 'CompleteRegistration',
        'initiate_checkout': 'InitiateCheckout'
        // Note: 'subscribe_success' is handled directly in subscribe-flow.js
        // to avoid duplicate events
      };
      return mapping[eventName] || null;
    },

    shouldSendMetaCAPI: function(eventName) {
      var capiEvents = {
        PageView: true,
        ViewContent: true,
        AddToCart: true,
        InitiateCheckout: true,
        Purchase: true,
        Lead: true,
        CompleteRegistration: true,
        Subscribe: true
      };
      return !!capiEvents[eventName];
    },

    /**
     * Prepare parameters for Meta Pixel
     */
    prepareMetaPixelParams: function(eventData) {
      // Meta Pixel expects specific parameter names
      var params = {};

      // Map common parameters
      if (eventData.page_title) params.content_name = eventData.page_title;
      if (eventData.page_url) params.content_url = eventData.page_url;
      if (eventData.button_id) params.content_category = eventData.button_id;
      if (eventData.section) {
        params.content_category = eventData.section;
        params.content_ids = [eventData.section];
      }
      if (eventData.items && eventData.items.length) {
        params.content_type = 'product';
        params.content_ids = eventData.items
          .map(function(item) { return item && item.item_id ? String(item.item_id) : ''; })
          .filter(Boolean);
        params.contents = eventData.items.map(function(item) {
          return {
            id: String(item.item_id || ''),
            quantity: item.quantity || 1,
            item_price: item.price || 0
          };
        }).filter(function(item) { return item.id; });
        params.num_items = eventData.items.reduce(function(total, item) {
          return total + (item.quantity || 1);
        }, 0);
      }
      if (eventData.transaction_id) params.order_id = String(eventData.transaction_id);
      if (eventData.value !== undefined) params.value = eventData.value;
      if (eventData.currency) params.currency = eventData.currency;

      // Add value and currency for conversion events
      if (eventData.event === 'subscribe_success') {
        params.value = eventData.value || 0;
        params.currency = 'USD';
        if (!params.content_name) params.content_name = 'Newsletter Subscription';
      }

      // Include session and visitor tracking
      if (eventData.session_id) params.session_id = eventData.session_id;
      if (eventData.visitor_id) params.visitor_id = eventData.visitor_id;

      // Include UTM parameters if available (critical for ad attribution)
      if (eventData.utm_source) params.utm_source = eventData.utm_source;
      if (eventData.utm_medium) params.utm_medium = eventData.utm_medium;
      if (eventData.utm_campaign) params.utm_campaign = eventData.utm_campaign;
      if (eventData.utm_content) params.utm_content = eventData.utm_content;
      if (eventData.utm_term) params.utm_term = eventData.utm_term;

      return params;
    },

    /**
     * Build GA4 e-commerce item(s) from cart item or product/variant
     * @param {Object|Array} item - Cart item { variant_id, product_id, title, price, quantity } or product { id, title, variants }
     * @param {Object} [variant] - For product: selected variant { id, price, title }
     * @param {number} [index] - Optional list index for view_item_list/select_item
     */
    buildGA4Items: function(item, variant, index) {
      var items = Array.isArray(item) ? item : [item];
      return items.map(function(it, i) {
        var idx = index !== undefined ? index : i;
        // Cart item from Shopify (cart add response or cart.items)
        if (it.variant_id != null) {
          return {
            item_id: String(it.variant_id),
            item_name: it.product_title || it.title,
            price: (it.price != null ? it.price : it.final_price) / 100,
            quantity: it.quantity || 1,
            index: idx
          };
        }
        // Product + variant (for view_item / select_item)
        if (it.id != null && variant) {
          return {
            item_id: String(variant.id),
            item_name: it.title,
            price: parseFloat(String(variant.price)) / 100 || 0,
            quantity: 1,
            index: idx
          };
        }
        return { item_id: String(it.id), item_name: it.title || '', price: 0, quantity: 1, index: idx };
      });
    },

    /**
     * GA4: view_item - Product detail view
     */
    trackViewItem: function(product, variant, currency) {
      if (!window.gtag) return;
      currency = currency || 'USD';
      var items = this.buildGA4Items(product, variant, 0);
      try {
        window.gtag('event', 'view_item', {
          currency: currency,
          value: (variant && variant.price) ? parseFloat(String(variant.price)) / 100 : 0,
          items: items
        });
        if (this.config.debug) console.log('[Analytics] GA4 view_item', items);
      } catch (e) {
        console.warn('[Analytics] GA4 view_item error:', e);
      }
    },

    /**
     * GA4: view_item_list - Collection/category view
     */
    trackViewItemList: function(listId, items, currency) {
      if (!window.gtag) return;
      currency = currency || 'USD';
      var ga4Items = Array.isArray(items) ? items.map(function(it, i) {
        return {
          item_id: String(it.variant_id || it.id),
          item_name: it.title || it.product_title,
          price: (it.price != null ? it.price : 0) / 100,
          quantity: 1,
          index: i
        };
      }) : [];
      try {
        window.gtag('event', 'view_item_list', {
          item_list_id: listId,
          item_list_name: listId,
          items: ga4Items
        });
        if (this.config.debug) console.log('[Analytics] GA4 view_item_list', listId, ga4Items.length);
      } catch (e) {
        console.warn('[Analytics] GA4 view_item_list error:', e);
      }
    },

    /**
     * GA4: select_item - Click product from list
     */
    trackSelectItem: function(listId, item, index) {
      if (!window.gtag) return;
      var ga4Items = this.buildGA4Items(item, null, index);
      if (ga4Items[0]) ga4Items[0].index = index;
      try {
        window.gtag('event', 'select_item', {
          item_list_id: listId,
          item_list_name: listId,
          items: ga4Items
        });
        if (this.config.debug) console.log('[Analytics] GA4 select_item', listId, ga4Items);
      } catch (e) {
        console.warn('[Analytics] GA4 select_item error:', e);
      }
    },

    /**
     * GA4: add_to_cart
     */
    trackAddToCart: function(items, value, currency) {
      currency = currency || 'USD';
      var ga4Items = this.buildGA4Items(items);
      if (window.gtag) {
        try {
          window.gtag('event', 'add_to_cart', {
            currency: currency,
            value: typeof value === 'number' ? value : 0,
            items: ga4Items
          });
          if (this.config.debug) console.log('[Analytics] GA4 add_to_cart', ga4Items);
        } catch (e) {
          console.warn('[Analytics] GA4 add_to_cart error:', e);
        }
      }
      this.track('add_to_cart', { value: value, currency: currency, items: ga4Items });
    },

    /**
     * GA4: remove_from_cart
     */
    trackRemoveFromCart: function(items, value, currency) {
      currency = currency || 'USD';
      var ga4Items = this.buildGA4Items(items);
      if (window.gtag) {
        try {
          window.gtag('event', 'remove_from_cart', {
            currency: currency,
            value: typeof value === 'number' ? value : 0,
            items: ga4Items
          });
          if (this.config.debug) console.log('[Analytics] GA4 remove_from_cart', ga4Items);
        } catch (e) {
          console.warn('[Analytics] GA4 remove_from_cart error:', e);
        }
      }
      this.track('remove_from_cart', { value: value, currency: currency, items: ga4Items });
    },

    /**
     * GA4: purchase - Call from thank you / order status page when order data is available
     */
    trackPurchase: function(transactionId, value, currency, items) {
      currency = currency || 'USD';
      var ga4Items = this.buildGA4Items(items || []);
      if (window.gtag) {
        try {
          window.gtag('event', 'purchase', {
            transaction_id: transactionId,
            value: typeof value === 'number' ? value : 0,
            currency: currency,
            items: ga4Items
          });
          if (this.config.debug) console.log('[Analytics] GA4 purchase', transactionId, ga4Items);
        } catch (e) {
          console.warn('[Analytics] GA4 purchase error:', e);
        }
      }
      this.track('purchase', { transaction_id: transactionId, value: value, currency: currency, items: ga4Items });
    },

    /**
     * Track page view
     *
     * IMPORTANT: PageView is already tracked automatically by each platform's own SDK:
     *   - GA4: gtag('config', ...) in theme.liquid auto-sends page_view
     *   - Meta Pixel: fbq('track', 'PageView') in theme.liquid
     *   - PTengine: PTengine SDK auto-tracks page views on script load
     *
     * This method only sends page_view to Klaviyo (which has no auto-tracking).
     * Do NOT add duplicate sends to GA4, Meta, or PTengine here.
     */
    trackPageView: function() {
      var eventData = {
        event: 'page_view',
        timestamp: new Date().toISOString(),
        session_id: this.config.sessionId,
        visitor_id: this.getVisitorId(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        referrer: document.referrer
      };

      // Send to Klaviyo only (Klaviyo has no auto page_view tracking)
      if (window._learnq) {
        try {
          window._learnq.push(['track', 'page_view', eventData]);
        } catch (e) {
          console.warn('[Analytics] Klaviyo error:', e);
        }
      }
    },

    /**
     * Initialize CTA click tracking
     */
    initCTATracking: function() {
      var self = this;

      document.addEventListener('click', function(e) {
        var cta = e.target.closest('[data-cta]');
        if (cta) {
          var section = cta.closest('[data-section]');
          self.track('cta_click', {
            button_id: cta.dataset.cta,
            button_text: (cta.textContent || '').trim().substring(0, 50),
            section: section ? section.dataset.section : 'unknown'
          });
        }
      });
    },

    /**
     * Initialize section visibility tracking
     */
    initSectionTracking: function() {
      var self = this;
      var sections = document.querySelectorAll('[data-section]');
      var trackedSections = {};

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              var sectionId = entry.target.dataset.section;
              if (!trackedSections[sectionId]) {
                trackedSections[sectionId] = true;
                self.track('section_view', {
                  section_id: sectionId
                });
              }
            }
          });
        }, { threshold: 0.3 });

        sections.forEach(function(section) {
          observer.observe(section);
        });
      }
    },

    /**
     * Initialize scroll depth tracking
     */
    initScrollTracking: function() {
      var self = this;
      var scrollDepths = [25, 50, 75, 90];
      var trackedDepths = {};

      var throttle = function(func, limit) {
        var inThrottle;
        return function() {
          var args = arguments;
          var context = this;
          if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(function() {
              inThrottle = false;
            }, limit);
          }
        };
      };

      var trackScroll = throttle(function() {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - window.innerHeight;
        var scrollPercent = Math.round((scrollTop / docHeight) * 100);

        scrollDepths.forEach(function(depth) {
          if (scrollPercent >= depth && !trackedDepths[depth]) {
            trackedDepths[depth] = true;
            self.track('scroll_depth', {
              depth: depth
            });
          }
        });
      }, 200);

      window.addEventListener('scroll', trackScroll, { passive: true });
    },

    /**
     * Track unique visitor (first visit)
     */
    trackUniqueVisitor: function() {
      try {
        var hasVisited = sessionStorage.getItem('memoket_visited');
        if (!hasVisited) {
          sessionStorage.setItem('memoket_visited', 'true');
          this.track('unique_visitor', {
            visitor_id: this.getVisitorId()
          });
        }
      } catch (e) {
        // SessionStorage not available
      }
    },

    /**
     * Track form submission
     */
    trackFormSubmit: function(formId, formType, success) {
      this.track('form_submit', {
        form_id: formId,
        form_type: formType,
        success: success
      });
      // Note: Meta Pixel events are now handled directly in subscribe-flow.js
      // to have better control and avoid duplicate events
    },

    /**
     * Track video start
     */
    trackVideoStart: function(videoId, videoTitle) {
      this.track('video_start', {
        video_id: videoId,
        video_title: videoTitle
      });
    },

    /**
     * Track video progress
     */
    trackVideoProgress: function(videoId, progress) {
      this.track('video_progress', {
        video_id: videoId,
        progress: progress
      });
    },

    /**
     * Track video completion
     */
    trackVideoComplete: function(videoId, duration) {
      this.track('video_complete', {
        video_id: videoId,
        duration: duration
      });
    },

    /**
     * Track section view time
     */
    trackSectionViewTime: function() {
      var self = this;
      var sections = document.querySelectorAll('[data-section]');
      var sectionTimers = {};

      if (!('IntersectionObserver' in window)) return;

      sections.forEach(function(section) {
        var sectionId = section.dataset.section;
        var startTime = null;

        var observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              startTime = Date.now();
            } else if (startTime !== null) {
              var duration = Date.now() - startTime;
              self.track('section_view_time', {
                section_id: sectionId,
                duration_ms: duration
              });
              startTime = null;
            }
          });
        }, { threshold: 0.5 });

        observer.observe(section);
      });
    },

    /**
     * Track form field interactions
     */
    trackFormField: function(action, fieldName, formId) {
      this.track('form_field', {
        action: action,
        field_name: fieldName,
        form_id: formId
      });
    },

    /**
     * Track time on page
     */
    trackTimeOnPage: function() {
      var self = this;
      var startTime = Date.now();
      var intervals = [30, 60, 120, 300]; // seconds
      var trackedIntervals = {};

      setInterval(function() {
        var elapsed = Math.floor((Date.now() - startTime) / 1000);

        intervals.forEach(function(interval) {
          if (elapsed >= interval && !trackedIntervals[interval]) {
            trackedIntervals[interval] = true;
            self.track('time_on_page', {
              seconds: interval
            });
          }
        });
      }, 5000);
    },

    /**
     * Enable debug mode
     */
    enableDebug: function() {
      this.config.debug = true;
      console.log('[Analytics] Debug mode enabled');
    },

    /**
     * Get cookie value by name (for Meta fbp/fbc cookies)
     */
    getCookie: function(name) {
      try {
        var value = '; ' + document.cookie;
        var parts = value.split('; ' + name + '=');
        if (parts.length === 2) {
          return parts.pop().split(';').shift();
        }
      } catch (e) {}
      return '';
    },

    /**
     * Send event to Meta Conversion API via AWS Lambda
     * This provides server-side redundancy alongside browser-side fbq() calls.
     * Meta uses eventID to deduplicate events sent from both Pixel and CAPI.
     *
     * @param {string} eventName - Meta standard event name (e.g. 'PageView', 'ViewContent')
     * @param {string} eventId - Must match the eventID used in the fbq() call
     * @param {Object} eventData - Original event data with UTM params etc.
     */
    sendToMetaCAPI: function(eventName, eventId, eventData) {
      try {
        var config = window.MemoketConfig;
        if (!config || !config.metaCapiEnabled || !config.metaCapiUrl) {
          return;
        }

        // Skip subscribe_success — handled by subscribe-flow.js to avoid duplicates
        if (eventData && eventData.event === 'subscribe_success') {
          return;
        }

        var fbp = this.getCookie('_fbp');
        var fbc = this.getCookie('_fbc');
        var utmParams = this.getUTMParams();

        var capiPayload = {
          event_name: eventName,
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          url: window.location.href,
          user_agent: navigator.userAgent,
          fbp: fbp || '',
          fbc: fbc || '',
          // UTM parameters for ad attribution
          utm_source: utmParams.utm_source || '',
          utm_medium: utmParams.utm_medium || '',
          utm_campaign: utmParams.utm_campaign || '',
          utm_content: utmParams.utm_content || '',
          utm_term: utmParams.utm_term || ''
        };

        // Add content data if available
        if (eventData) {
          if (eventData.section) capiPayload.section = eventData.section;
          if (eventData.page_title) capiPayload.content_name = eventData.page_title;
          if (eventData.button_id) capiPayload.content_category = eventData.button_id;
          if (eventData.transaction_id) capiPayload.order_id = String(eventData.transaction_id);
          if (eventData.value !== undefined) capiPayload.value = eventData.value;
          if (eventData.currency) capiPayload.currency = eventData.currency;
          if (eventData.items && eventData.items.length) {
            capiPayload.content_type = 'product';
            capiPayload.content_ids = eventData.items
              .map(function(item) { return item && item.item_id ? String(item.item_id) : ''; })
              .filter(Boolean);
            capiPayload.contents = eventData.items.map(function(item) {
              return {
                id: String(item.item_id || ''),
                quantity: item.quantity || 1,
                item_price: item.price || 0
              };
            }).filter(function(item) { return item.id; });
            capiPayload.num_items = eventData.items.reduce(function(total, item) {
              return total + (item.quantity || 1);
            }, 0);
          }
        }

        if (config.metaCapiDebug) {
          console.log('[Analytics] Sending to Meta CAPI:', {
            event_name: eventName,
            event_id: eventId,
            has_fbp: !!fbp,
            has_fbc: !!fbc,
            url: config.metaCapiUrl
          });
        }

        fetch(config.metaCapiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(capiPayload),
          keepalive: true
        })
        .then(function(response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function(data) {
          if (config.metaCapiDebug) {
            console.log('[Analytics] ✓ Meta CAPI success:', eventName, data);
          }
        })
        .catch(function(error) {
          if (config.metaCapiDebug) {
            console.warn('[Analytics] Meta CAPI error:', eventName, error);
          }
        });
      } catch (e) {
        // Silent failure — never impact user experience
      }
    },

    /**
     * Append persisted UTM params to all same-origin <a> links and <form> actions
     * so UTM survives page navigations even without sessionStorage.
     *
     * Runs once on init. Uses event delegation (click / submit) so it also
     * catches dynamically-added elements (e.g. AJAX cart drawer).
     */
    initUTMForwarding: function() {
      var self = this;

      /**
       * Append UTM query-strings to a URL string and return the new URL.
       * Skips if URL already contains utm_source (avoid duplicating).
       */
      function appendUTM(href) {
        try {
          var utmParams = self.getUTMParams();
          // Nothing to append
          if (!utmParams.utm_source) return href;

          var url = new URL(href, window.location.origin);

          // Only touch same-origin links (skip external / CDN / Shopify checkout etc.)
          if (url.origin !== window.location.origin) return href;

          // Skip if the URL already carries utm_source
          if (url.searchParams.has('utm_source')) return href;

          // Skip asset / static file links
          if (/\.(js|css|png|jpe?g|gif|svg|webp|mp4|webm|woff2?|ttf|eot|ico)(\?|$)/i.test(url.pathname)) return href;

          var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
          keys.forEach(function(k) {
            if (utmParams[k]) {
              url.searchParams.set(k, utmParams[k]);
            }
          });
          return url.toString();
        } catch (e) {
          return href;
        }
      }

      // --- Intercept link clicks (event delegation) ---
      document.addEventListener('click', function(e) {
        var anchor = e.target.closest('a[href]');
        if (!anchor) return;
        var href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
        var newHref = appendUTM(href);
        if (newHref !== href) {
          anchor.setAttribute('href', newHref);
        }
      }, true); // capture phase so we modify before the navigation happens

      // --- Intercept form submissions (event delegation) ---
      document.addEventListener('submit', function(e) {
        var form = e.target.closest('form');
        if (!form) return;
        var action = form.getAttribute('action');
        if (!action) return;
        var newAction = appendUTM(action);
        if (newAction !== action) {
          form.setAttribute('action', newAction);
        }
      }, true);

      if (this.config.debug) {
        console.log('[Analytics] UTM forwarding initialized');
      }
    }
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      MemoketAnalytics.init();
    });
  } else {
    MemoketAnalytics.init();
  }

  // Expose globally as Analytics (for video-player compatibility)
  window.MemoketAnalytics = MemoketAnalytics;
  window.Analytics = MemoketAnalytics;
})();
